import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { setCartCount } from '../../../properties/property-view/slice/OrderCartSlice';
import {
  useGetCartQuery,
  useUpdateCartMutation,
  useDeleteCartMutation,
  useDeleteCartItemsMutation,
  useDeleteAssetOrderMutation,
} from '../../services/orderApi';
import {
  PropertyCartItem,
  AssetCartItem,
  UseCartReturn,
} from '../types/cart.types';
import { getPropertyAddress } from '../../../../services/property-address';
import {
  flattenGroupedCart,
  stripLocalFields,
  calcSubtotal,
  calcPropertyInitialPrice,
  calcAssetInitialPrice,
  groupByEntity,
  hasMultipleEntityUUIDs,
  applyAssetGroupEdit,
  assetLineKey,
} from '../utils/cartTransforms';

export const useCart = (): UseCartReturn => {
  const dispatch = useDispatch();
  const cartCount = useSelector(
    (state: RootState) => state.orderCart.cartCount
  );

  const userId = localStorage.getItem('userId') ?? '';

  // Local state holds the optimistic view of the cart so delete / update
  // flows can roll back on failure. RTK Query is the source of truth on
  // initial fetch and after invalidation; the effect below reconciles into
  // local state whenever the query data changes.
  const [cartId, setCartId] = useState('');
  const [propertyItems, setPropertyItems] = useState<PropertyCartItem[]>([]);
  const [assetItems, setAssetItems] = useState<AssetCartItem[]>([]);
  const [mutatingItemIds, setMutatingItemIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const {
    data: cartResponse,
    isLoading: isInitialLoading,
    isError: isCartError,
    refetch: refetchCartQuery,
  } = useGetCartQuery(userId, { skip: !userId });

  const [updateCartMutation] = useUpdateCartMutation();
  const [deleteCartMutation] = useDeleteCartMutation();
  const [deleteCartItemsMutation] = useDeleteCartItemsMutation();
  const [deleteAssetOrderMutation] = useDeleteAssetOrderMutation();

  const startMutation = useCallback((id: string) => {
    setMutatingItemIds((prev) => new Set(prev).add(id));
  }, []);

  const endMutation = useCallback((id: string) => {
    setMutatingItemIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Reconcile RTK Query response into local state. flattenGroupedCart and
  // the address mapping both produce fresh arrays / objects, so the frozen
  // RTK Query payload never enters local state directly.
  useEffect(() => {
    if (cartResponse) {
      setCartId(cartResponse.cartId);
      const { propertyItems: rawProp, assetItems: rawAsset } =
        flattenGroupedCart(cartResponse.groupedCart);
      setPropertyItems(
        rawProp.map((item) => ({
          ...item,
          formattedAddress: getPropertyAddress(item),
        }))
      );
      setAssetItems(rawAsset.map((item) => ({ ...item })));
      dispatch(setCartCount(rawProp.length + rawAsset.length));
    } else if (!isInitialLoading) {
      setCartId('');
      setPropertyItems([]);
      setAssetItems([]);
      dispatch(setCartCount(0));
    }
  }, [cartResponse, isInitialLoading, dispatch]);

  useEffect(() => {
    if (isCartError) setError('Failed to load cart data');
  }, [isCartError]);

  // Derived totals
  const propertySubtotal = useMemo(() => calcSubtotal(propertyItems), [propertyItems]);
  const assetSubtotal = useMemo(() => calcSubtotal(assetItems), [assetItems]);
  const totalPrice = propertySubtotal + assetSubtotal;
  const totalInitialPrice = useMemo(
    () => calcPropertyInitialPrice(propertyItems) + calcAssetInitialPrice(assetItems),
    [propertyItems, assetItems]
  );
  const totalItemCount = propertyItems.length + assetItems.length;
  const totalSavingPrice = totalInitialPrice - totalPrice;

  // Refetch when cart count changes externally (e.g. item added on another page).
  useEffect(() => {
    if (
      userId &&
      cartCount !== totalItemCount &&
      !isInitialLoading &&
      cartResponse !== undefined
    ) {
      refetchCartQuery();
    }
  }, [cartCount, totalItemCount, isInitialLoading, cartResponse, userId, refetchCartQuery]);

  const removePropertyItem = useCallback(
    async (propertyId: string): Promise<void> => {
      startMutation(propertyId);
      const previousItems = propertyItems;
      const updatedItems = propertyItems.filter((i) => i.propertyId !== propertyId);
      try {
        setPropertyItems(updatedItems);
        dispatch(setCartCount(updatedItems.length + assetItems.length));
        await deleteCartItemsMutation({ userId, lineItemUUIDs: propertyId }).unwrap();
      } catch (err) {
        setPropertyItems(previousItems);
        dispatch(setCartCount(previousItems.length + assetItems.length));
        setError('Failed to remove item');
        console.error('Error removing property item:', err);
        throw err;
      } finally {
        endMutation(propertyId);
      }
    },
    [propertyItems, assetItems, dispatch, startMutation, endMutation, deleteCartItemsMutation, userId]
  );

  const removeAssetItem = useCallback(
    async (lineItemUUID: string | undefined, assetId: string, slug: string): Promise<void> => {
      const lineKey = assetLineKey(lineItemUUID, assetId, slug);
      startMutation(lineKey);
      const prevAssets = assetItems;
      // Match by the stable lineItemUUID when present; fall back to the (assetId, slug) composite
      // for a legacy line that lacks one (mirrors the backend's delete fallback).
      const updated = assetItems.filter((i) =>
        lineItemUUID
          ? i.lineItemUUID !== lineItemUUID
          : !(i.assetId === assetId && i.service?.shoppingCartSlug === slug),
      );
      try {
        setAssetItems(updated);
        dispatch(setCartCount(propertyItems.length + updated.length));
        await deleteAssetOrderMutation({ userId, lineItemUUID, assetId, slug }).unwrap();
      } catch (err) {
        setAssetItems(prevAssets);
        dispatch(setCartCount(propertyItems.length + prevAssets.length));
        setError('Failed to remove item');
        console.error('Error removing asset item:', err);
        throw err;
      } finally {
        endMutation(lineKey);
      }
    },
    [propertyItems, assetItems, dispatch, startMutation, endMutation, deleteAssetOrderMutation, userId],
  );

  const clearCart = useCallback(async (): Promise<void> => {
    const CLEAR_CART_ID = 'clear-cart';
    startMutation(CLEAR_CART_ID);
    const prevProps = propertyItems;
    const prevAssets = assetItems;
    const prevCartId = cartId;
    try {
      setCartId('');
      setPropertyItems([]);
      setAssetItems([]);
      dispatch(setCartCount(0));
      await deleteCartMutation(userId).unwrap();
    } catch (err) {
      setCartId(prevCartId);
      setPropertyItems(prevProps);
      setAssetItems(prevAssets);
      dispatch(setCartCount(prevProps.length + prevAssets.length));
      setError('Failed to clear cart');
      console.error('Error clearing cart:', err);
      throw err;
    } finally {
      endMutation(CLEAR_CART_ID);
    }
  }, [propertyItems, assetItems, cartId, dispatch, startMutation, endMutation, deleteCartMutation, userId]);

  const updatePropertyItem = useCallback(
    async (updatedItem: PropertyCartItem): Promise<void> => {
      startMutation(updatedItem.propertyId);
      const previousItems = propertyItems;
      const updatedItems = propertyItems.map((item) =>
        item.propertyId === updatedItem.propertyId ? { ...item, ...updatedItem } : item
      );
      const itemsForApi = updatedItems.map(stripLocalFields);
      try {
        setPropertyItems(updatedItems);
        await updateCartMutation({ cartId, userId, items: itemsForApi }).unwrap();
      } catch (err) {
        setPropertyItems(previousItems);
        setError('Failed to update item');
        console.error('Error updating property item:', err);
        throw err;
      } finally {
        endMutation(updatedItem.propertyId);
      }
    },
    [cartId, userId, propertyItems, startMutation, endMutation, updateCartMutation]
  );

  const updateAssetGroup = useCallback(
    async ({
      lineItemUUID,
      lineAssetId,
      lineSlug,
      removedRefIds,
      note,
    }: { lineItemUUID?: string; lineAssetId: string; lineSlug: string; removedRefIds: string[]; note: string }): Promise<void> => {
      const lineKey = assetLineKey(lineItemUUID, lineAssetId, lineSlug);
      startMutation(lineKey);
      const prevAssets = assetItems;
      const updated = applyAssetGroupEdit(assetItems, { lineItemUUID, lineAssetId, lineSlug, removedRefIds, note });
      const itemsForApi = propertyItems.map(stripLocalFields);
      try {
        setAssetItems(updated);
        await updateCartMutation({ cartId, userId, items: itemsForApi, editPhotoOrders: updated }).unwrap();
      } catch (err) {
        setAssetItems(prevAssets);
        setError('Failed to update item');
        console.error('Error updating asset group:', err);
        throw err;
      } finally {
        endMutation(lineKey);
      }
    },
    [cartId, userId, propertyItems, assetItems, startMutation, endMutation, updateCartMutation],
  );

  const refetch = useCallback(async () => {
    await refetchCartQuery();
  }, [refetchCartQuery]);

  const propertyItemsByEntity = useMemo(() => groupByEntity(propertyItems), [propertyItems]);
  const hasMultipleEntities = useMemo(
    () => hasMultipleEntityUUIDs(propertyItems, assetItems),
    [propertyItems, assetItems]
  );

  return {
    cartId, propertyItems, assetItems,
    totalPrice, totalInitialPrice, propertySubtotal, assetSubtotal,
    totalItemCount, totalSavingPrice,
    propertyItemsByEntity, hasMultipleEntities,
    isInitialLoading, mutatingItemIds, error,
    removePropertyItem, removeAssetItem, clearCart,
    updatePropertyItem, updateAssetGroup, refetch
  };
};
